import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { AccountService } from '../../account.service';

@Component({
  selector: 'app-mtls-certificate',
  templateUrl: './mtls-certificate.component.html',
  styleUrls: ['./mtls-certificate.component.scss'],
})
export class MtlsCertificateComponent implements OnChanges {
  @Input() account: any;

  public hasMtlsCertificate: boolean = false;
  public isGeneratingCertificate: boolean = false;
  public isRotatingCertificate: boolean = false;
  public certificateExpiresAt: Date | null = null;

  constructor(
    private accountService: AccountService,
    private toastr: ToastrService,
    private modalService: NgbModal,
    private translate: TranslateService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['account'] && this.account) {
      this.loadMtlsCertificateStatus();
    }
  }

  get isMtlsEnabled(): boolean {
    return Array.isArray(this.account?.mtls) && this.account.mtls.length > 0;
  }

  get statusIcon(): string {
    if (this.hasMtlsCertificate) return 'verified';
    if (!this.isMtlsEnabled) return 'block';
    return 'pending';
  }

  get statusLabel(): string {
    if (this.hasMtlsCertificate) return 'Certificado ativo';
    if (!this.isMtlsEnabled) return 'Não habilitado';
    return 'Aguardando geração';
  }

  get statusBadgeClass(): string {
    if (this.hasMtlsCertificate) return 'bg-success';
    if (!this.isMtlsEnabled) return 'bg-light text-muted border';
    return 'bg-warning text-dark';
  }

  get generateCertificateLabel(): string {
    const key = 'common.generateCertificate';
    const translated = this.translate.instant(key);
    return translated === key ? 'Gerar certificado' : translated;
  }

  get rotateCertificateLabel(): string {
    const key = 'common.rotateCertificate';
    const translated = this.translate.instant(key);
    return translated === key ? 'Rotacionar certificado' : translated;
  }

  get daysUntilExpiration(): number | null {
    if (!this.certificateExpiresAt) return null;
    const now = new Date();
    const diff = this.certificateExpiresAt.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }

  get isExpirationNear(): boolean {
    return this.daysUntilExpiration !== null && this.daysUntilExpiration <= 90;
  }

  get isExpired(): boolean {
    return this.daysUntilExpiration !== null && this.daysUntilExpiration <= 0;
  }

  get formattedExpirationDate(): string {
    if (!this.certificateExpiresAt) return '';
    return this.certificateExpiresAt.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  get expirationMessage(): string {
    if (this.isExpired)
      return 'Certificado expirado. Gere um novo certificado via rotação.';
    if (this.daysUntilExpiration === 1)
      return 'Expira amanhã. Rotacione o certificado o mais rápido possível.';
    return (
      'Expiração próxima (' +
      this.daysUntilExpiration +
      ' dias restantes). Rotacione o certificado em breve.'
    );
  }

  get isGenerateCertificateDisabled(): boolean {
    return (
      !this.isMtlsEnabled ||
      this.hasMtlsCertificate ||
      this.isGeneratingCertificate
    );
  }

  get generateCertificateHint(): string | null {
    if (this.hasMtlsCertificate) {
      return this.translate.instant('common.certificateGeneratedOnlyRotate');
    }

    if (!this.isMtlsEnabled) {
      return this.translate.instant('common.mtlsNotEnabledForAccount');
    }

    return null;
  }

  downloadMtlsCertificate(): void {
    if (!this.isMtlsEnabled) {
      this.toastr.warning(
        this.translate.instant('common.mtlsNotEnabledForAccount'),
        'Aviso',
        { timeOut: 2000, positionClass: 'toast-top-right' },
      );
      return;
    }

    if (this.hasMtlsCertificate) {
      this.toastr.warning(
        this.translate.instant('common.certificateGeneratedOnlyRotate'),
        'Aviso',
        { timeOut: 2000, positionClass: 'toast-top-right' },
      );
      return;
    }

    this.isGeneratingCertificate = true;
    this.accountService.createMtlsCertificate().subscribe({
      next: (response) => {
        this.isGeneratingCertificate = false;

        if (!response?.pfxBase64 || typeof response.pfxBase64 !== 'string') {
          this.toastr.warning(
            this.translate.instant('common.mtlsGenerateFailed'),
            'Aviso',
            { timeOut: 2000, positionClass: 'toast-top-right' },
          );
          return;
        }

        try {
          const fileName = `${this.account?.id || 'certificate'}.p12`;
          const blob = this.createP12BlobFromBase64(response.pfxBase64);
          this.downloadBlob(blob, fileName);
          this.hasMtlsCertificate = true;

          this.toastr.success(
            this.translate.instant('common.mtlsGeneratedSuccess'),
            'Sucesso',
            { timeOut: 2000, positionClass: 'toast-top-right' },
          );
        } catch {
          this.toastr.error(
            this.translate.instant('common.mtlsDownloadError'),
            'Erro',
            { timeOut: 2000, positionClass: 'toast-top-right' },
          );
        }
      },
      error: (err) => {
        this.isGeneratingCertificate = false;
        if (err?.status === 403) {
          this.toastr.error(
            'Você não possui permissão para gerar o certificado.',
            'Acesso negado',
            { timeOut: 5000, positionClass: 'toast-top-right' },
          );
        } else {
          this.toastr.error(
            this.translate.instant('common.mtlsGenerateFailed'),
            'Erro',
            { timeOut: 4000, positionClass: 'toast-top-right' },
          );
        }
      },
    });
  }

  openRotateCertificateConfirm(content: any): void {
    this.modalService.open(content, {
      centered: true,
      size: 'md',
      backdrop: 'static',
      keyboard: false,
    });
  }

  rotateMtlsCertificate(modal: any): void {
    modal.close();
    this.isRotatingCertificate = true;

    this.accountService.rotateMtlsCertificate().subscribe({
      next: (response) => {
        this.isRotatingCertificate = false;

        if (!response?.pfxBase64 || typeof response.pfxBase64 !== 'string') {
          this.toastr.warning(
            this.translate.instant('common.mtlsRotateFailed'),
            'Aviso',
            { timeOut: 2000, positionClass: 'toast-top-right' },
          );
          return;
        }

        try {
          const fileName = `${this.account?.id || 'certificate'}.p12`;
          const blob = this.createP12BlobFromBase64(response.pfxBase64);
          this.downloadBlob(blob, fileName);
          this.hasMtlsCertificate = true;

          this.toastr.success(
            this.translate.instant('common.mtlsRotatedSuccess'),
            'Sucesso',
            { timeOut: 2000, positionClass: 'toast-top-right' },
          );
        } catch {
          this.toastr.error(
            this.translate.instant('common.mtlsDownloadError'),
            'Erro',
            { timeOut: 2000, positionClass: 'toast-top-right' },
          );
        }
      },
      error: (err) => {
        this.isRotatingCertificate = false;
        if (err?.status === 403) {
          this.toastr.error(
            'Você não possui permissão para rotacionar o certificado.',
            'Acesso negado',
            { timeOut: 4000, positionClass: 'toast-top-right' },
          );
        } else {
          this.toastr.error(
            this.translate.instant('common.mtlsRotateFailed'),
            'Erro',
            { timeOut: 2000, positionClass: 'toast-top-right' },
          );
        }
      },
    });
  }

  private loadMtlsCertificateStatus(): void {
    if (!this.isMtlsEnabled) {
      this.hasMtlsCertificate = false;
      this.certificateExpiresAt = null;
      return;
    }

    this.accountService.getMtlsCertificate().subscribe({
      next: (response) => {
        this.hasMtlsCertificate = true;
        this.certificateExpiresAt = response?.expiresAt
          ? new Date(response.expiresAt)
          : null;
      },
      error: () => {
        this.hasMtlsCertificate = false;
        this.certificateExpiresAt = null;
      },
    });
  }

  private createP12BlobFromBase64(pfxBase64: string): Blob {
    const normalized = pfxBase64
      .replace(/^data:.*;base64,/, '')
      .replace(/\s/g, '');

    const binary = atob(normalized);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    return new Blob([bytes], { type: 'application/x-pkcs12' });
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = blobUrl;
    anchor.download = fileName;
    anchor.style.display = 'none';

    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();

    setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
  }
}
